import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowDown,
  ArrowUp,
  GitBranch,
  GripVertical,
  Plus,
  Save,
  Trash2,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  createWorkflowTemplate,
  deleteWorkflowTemplate,
  fetchWorkflowCatalog,
  updateWorkflowTemplate,
  validateWorkflowTemplate,
} from '@/lib/agentStudioApi';
import { usePermissions } from '@/contexts/PermissionsContext';
import { permissionDeniedTitle } from '@/lib/permissionUi';
import {
  defaultBuiltinForAgent,
  isStudioPaletteBlock,
  simpleStarterForAgent,
  findTemplate,
  findTemplateById,
  isReadOnlyBuiltinView,
  insertStepAt,
  moveSteps,
  normalizeStep,
  reorderStep,
  slugify,
  templateUiKey,
  WORKFLOW_BLOCK_MIME,
  WORKFLOW_STEP_MIME,
} from '@/lib/workflowTemplateUi';

const WORKFLOW_AGENT_KINDS = [
  { id: 'nup', label: 'NUP', newLabel: 'Custom provisioning' },
  { id: 'ter', label: 'TER', newLabel: 'Custom termination' },
  { id: 'pwd_reset', label: 'Password reset', newLabel: 'Custom password reset' },
  { id: 'access_mod', label: 'Access mod', newLabel: 'Custom access modification' },
  { id: 'ffid', label: 'FFID', newLabel: 'Custom FFID assignment' },
  { id: 'pamter', label: 'PAMTER', newLabel: 'Custom PAM offboarding' },
];

const EMPTY_FORM = {
  template_id: '',
  agent: 'nup',
  label: '',
  description: '',
  require_user_confirmation: true,
  allow_scheduled_termination: true,
  prefer_sod_check: false,
  steps: [],
};

export default function WorkflowTemplates() {
  const { hasPermission } = usePermissions();
  const canUpdate = hasPermission('agent-studio', 'update');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [isNew, setIsNew] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [dropTargetIndex, setDropTargetIndex] = useState(null);
  const [sequenceDragActive, setSequenceDragActive] = useState(false);

  const blockCatalog = useMemo(() => {
    const blocks = catalog?.blocks || {};
    return Object.values(blocks);
  }, [catalog]);

  const blocksForAgent = useCallback(
    (agent) =>
      blockCatalog.filter(
        (block) =>
          isStudioPaletteBlock(block)
          && (!agent || (block.agents || []).includes(agent)),
      ),
    [blockCatalog],
  );

  const applyTemplateToForm = useCallback((template) => {
    if (!template) return;
    setSelectedId(templateUiKey(template));
    setIsNew(false);
    setValidationErrors([]);
    setForm({
      template_id: template.template_id,
      agent: template.agent || template.agent_kind || 'nup',
      label: template.label || template.template_id,
      description: template.description || '',
      require_user_confirmation: template.require_user_confirmation !== false,
      allow_scheduled_termination: template.allow_scheduled_termination !== false,
      prefer_sod_check: Boolean(template.prefer_sod_check),
      steps: (template.steps || []).map(normalizeStep),
    });
  }, []);

  const initialSelectionDone = useRef(false);

  /** @param {{ refreshListOnly?: boolean }} [options] */
  const load = useCallback(async (options = {}) => {
    const { refreshListOnly = false } = options;
    if (!refreshListOnly) {
      setLoading(true);
    }
    try {
      const data = await fetchWorkflowCatalog();
      setCatalog(data);
      const rows = data.templates || [];
      setTemplates(rows);
      if (!refreshListOnly && !initialSelectionDone.current && rows.length) {
        initialSelectionDone.current = true;
        const first = defaultBuiltinForAgent(rows, 'nup') || rows[0];
        applyTemplateToForm(first);
      }
      return rows;
    } catch (error) {
      console.error('Failed to load workflow templates', error);
      alert('Could not load workflow templates. Is master-agent running?');
      return [];
    } finally {
      if (!refreshListOnly) {
        setLoading(false);
      }
    }
  }, [applyTemplateToForm]);

  useEffect(() => {
    load();
  }, [load]);

  const selectedTemplate = useMemo(
    () => findTemplate(templates, selectedId),
    [templates, selectedId],
  );
  const readOnlyBuiltin = isReadOnlyBuiltinView({ isNew, selectedTemplate });

  const selectTemplate = (template) => {
    applyTemplateToForm(template);
  };

  const startNew = (agent = 'nup', { seedTemplate = null } = {}) => {
    const meta = WORKFLOW_AGENT_KINDS.find((a) => a.id === agent) || WORKFLOW_AGENT_KINDS[0];
    const existingIds = new Set(
      (templates || []).map((t) => String(t.template_id || '').toLowerCase()),
    );
    let base = `${agent}_custom`;
    let suffix = 2;
    while (existingIds.has(base)) {
      base = `${agent}_custom_${suffix}`;
      suffix += 1;
    }
    setIsNew(true);
    setSelectedId('');
    setValidationErrors([]);
    const seed =
      seedTemplate
      || simpleStarterForAgent(templates, agent)
      || defaultBuiltinForAgent(templates, agent);
    setForm({
      ...EMPTY_FORM,
      template_id: base,
      agent,
      label: meta.newLabel,
      description: seed?.description || '',
      require_user_confirmation: seed?.require_user_confirmation !== false,
      allow_scheduled_termination: seed?.allow_scheduled_termination !== false,
      prefer_sod_check: Boolean(seed?.prefer_sod_check),
      steps: (seed?.steps || []).map(normalizeStep),
    });
  };

  const updateStep = (index, patch) => {
    setForm((prev) => {
      const steps = [...prev.steps];
      steps[index] = { ...steps[index], ...patch };
      return { ...prev, steps };
    });
  };

  const moveStep = (index, direction) => {
    setForm((prev) => ({
      ...prev,
      steps: moveSteps(prev.steps, index, direction),
    }));
  };

  const removeStep = (index) => {
    setForm((prev) => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index),
    }));
  };

  const addStep = () => {
    const options = blocksForAgent(form.agent);
    const first = options[0]?.block_id || 'extract_fields';
    setForm((prev) => ({
      ...prev,
      steps: insertStepAt(prev.steps, prev.steps.length, first),
    }));
  };

  const insertBlockAt = (index, blockId) => {
    if (!blockId || readOnlyBuiltin) return;
    setForm((prev) => ({
      ...prev,
      steps: insertStepAt(prev.steps, index, blockId),
    }));
  };

  const endSequenceDrag = () => {
    setSequenceDragActive(false);
    setDropTargetIndex(null);
  };

  const handleSequenceDrop = (event, index) => {
    event.preventDefault();
    event.stopPropagation();
    endSequenceDrag();
    if (readOnlyBuiltin) return;
    const blockId = event.dataTransfer.getData(WORKFLOW_BLOCK_MIME);
    const stepIndexRaw = event.dataTransfer.getData(WORKFLOW_STEP_MIME);
    if (blockId) {
      insertBlockAt(index, blockId);
      return;
    }
    if (stepIndexRaw !== '') {
      const from = Number.parseInt(stepIndexRaw, 10);
      if (!Number.isNaN(from)) {
        setForm((prev) => ({
          ...prev,
          steps: reorderStep(prev.steps, from, index),
        }));
      }
    }
  };

  const allowDrop = (event) => {
    if (readOnlyBuiltin) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const startPaletteDrag = (event, blockId) => {
    if (readOnlyBuiltin) return;
    event.dataTransfer.setData(WORKFLOW_BLOCK_MIME, blockId);
    event.dataTransfer.effectAllowed = 'copyMove';
    setSequenceDragActive(true);
  };

  const startStepDrag = (event, index) => {
    if (readOnlyBuiltin) return;
    event.dataTransfer.setData(WORKFLOW_STEP_MIME, String(index));
    event.dataTransfer.effectAllowed = 'move';
    setSequenceDragActive(true);
  };

  const renderInsertSlot = (index) => {
    const active = dropTargetIndex === index;
    const showRail = sequenceDragActive && !readOnlyBuiltin;
    return (
      <div
        key={`insert-${index}`}
        data-testid={`workflow-insert-slot-${index}`}
        className={`flex items-center justify-center rounded-md transition-all ${
          active
            ? 'min-h-10 border-2 border-dashed border-primary bg-primary/10 my-1'
            : showRail
              ? 'min-h-6 my-0.5 border border-dashed border-muted-foreground/40'
              : 'min-h-1 my-0.5'
        }`}
        onDragOver={(event) => {
          event.stopPropagation();
          allowDrop(event);
          setDropTargetIndex(index);
        }}
        onDrop={(event) => handleSequenceDrop(event, index)}
      >
        {active ? (
          <span className="text-xs font-medium text-primary">Drop here</span>
        ) : showRail ? (
          <span className="text-[10px] text-muted-foreground">Insert here</span>
        ) : null}
      </div>
    );
  };

  const duplicateFromBuiltin = (template) => {
    if (!template?.is_builtin) return;
    const agent = template.agent || template.agent_kind || 'nup';
    const slug = slugify(template.label || template.template_id);
    setIsNew(true);
    setSelectedId('');
    setValidationErrors([]);
    setForm({
      template_id: `${slug || agent}_copy`,
      agent,
      label: `${template.label || template.template_id} (copy)`,
      description: template.description || '',
      require_user_confirmation: template.require_user_confirmation !== false,
      allow_scheduled_termination: template.allow_scheduled_termination !== false,
      prefer_sod_check: Boolean(template.prefer_sod_check),
      steps: (template.steps || []).map(normalizeStep),
    });
  };

  const handleSave = async () => {
    if (!canUpdate) {
      alert("You don't have permission to save workflow templates (agent-studio update scope required).");
      return;
    }
    if (readOnlyBuiltin) return;
    setSaving(true);
    setValidationErrors([]);
    try {
      const payload = {
        template_id: form.template_id.trim(),
        agent: form.agent,
        label: form.label.trim(),
        description: form.description.trim(),
        require_user_confirmation: form.require_user_confirmation,
        allow_scheduled_termination: form.allow_scheduled_termination,
        prefer_sod_check: form.prefer_sod_check,
        steps: form.steps.map((step) => ({
          block_id: step.block_id,
          ...(step.note ? { note: step.note } : {}),
          ...(step.optional ? { optional: true } : {}),
        })),
      };
      const validation = await validateWorkflowTemplate(payload);
      if (!validation.valid) {
        setValidationErrors(validation.errors || []);
        return;
      }
      if (isNew) {
        await createWorkflowTemplate(payload);
      } else {
        await updateWorkflowTemplate(form.template_id, payload);
      }
      const savedId = payload.template_id;
      const rows = await load({ refreshListOnly: true });
      const saved = findTemplateById(rows, savedId);
      if (saved) {
        applyTemplateToForm(saved);
      } else {
        setIsNew(false);
        setSelectedId(savedId);
      }
    } catch (error) {
      console.error('Save failed', error);
      alert(error.response?.data?.detail || error.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!canUpdate || isNew || !form.template_id) return;
    if (selectedTemplate?.is_builtin) {
      alert('Built-in templates cannot be deleted.');
      return;
    }
    if (!window.confirm(`Delete template "${form.template_id}"?`)) return;
    setSaving(true);
    try {
      await deleteWorkflowTemplate(form.template_id);
      setSelectedId('');
      setForm(EMPTY_FORM);
      setIsNew(false);
      initialSelectionDone.current = false;
      await load();
    } catch (error) {
      console.error('Delete failed', error);
      alert(error.response?.data?.detail || error.message || 'Failed to delete template');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg">Loading workflow templates…</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-1">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <GitBranch className="h-8 w-8" />
            Workflow Templates
          </h1>
          <p className="text-muted-foreground mt-1 max-w-2xl">
            Built-in defaults match Halo&apos;s current workflow agents (read-only). Duplicate or create
            from a default, then assign templates in{' '}
            <Link to="/agent-studio" className="underline">
              Agent Studio
            </Link>
            .
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {WORKFLOW_AGENT_KINDS.map((kind) => (
            <Button
              key={kind.id}
              variant="outline"
              type="button"
              data-testid={`new-template-${kind.id}`}
              onClick={() => startNew(kind.id)}
            >
              New {kind.label} template
            </Button>
          ))}
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(200px,240px)_minmax(240px,300px)_minmax(0,1fr)] lg:grid-cols-[240px_minmax(0,1fr)]">
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-base">Saved templates</CardTitle>
            <CardDescription>Built-in + custom</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {isNew && (
              <div
                className="rounded-lg border border-dashed border-primary px-3 py-2 text-sm text-muted-foreground"
                data-testid="workflow-template-draft-banner"
              >
                Draft — not saved yet ({form.agent.toUpperCase()})
              </div>
            )}
            {templates.map((template) => (
              <button
                key={templateUiKey(template)}
                type="button"
                data-testid={`template-row-${templateUiKey(template)}`}
                onClick={() => selectTemplate(template)}
                className={`w-full rounded-lg border px-3 py-2 text-left transition ${
                  selectedId === templateUiKey(template) && !isNew
                    ? 'border-primary bg-muted'
                    : 'hover:bg-muted/60'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-sm">{template.label || template.template_id}</span>
                  {template.is_builtin ? (
                    <Badge variant="secondary">{template.is_default ? 'Default' : 'Built-in'}</Badge>
                  ) : (
                    <Badge>Custom</Badge>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {(template.agent || template.agent_kind || '').toUpperCase()} · {template.template_id}
                </div>
              </button>
            ))}
          </CardContent>
        </Card>

        <Card className="hidden xl:block" data-testid="workflow-block-palette">
          <CardHeader>
            <CardTitle className="text-base">Blocks</CardTitle>
            <CardDescription>
              Drag into the sequence for {form.agent.toUpperCase()}. Use ↑↓ if you prefer.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 max-h-[min(70vh,640px)] overflow-y-auto">
            {blocksForAgent(form.agent).length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No blocks loaded — check Agent Studio / master-agent catalog API.
              </p>
            ) : (
              blocksForAgent(form.agent).map((block) => (
                <div
                  key={block.block_id}
                  draggable={!readOnlyBuiltin}
                  onDragStart={(e) => startPaletteDrag(e, block.block_id)}
                  onDragEnd={endSequenceDrag}
                  data-testid={`palette-block-${block.block_id}`}
                  className={`rounded-lg border px-3 py-2 text-sm ${
                    readOnlyBuiltin
                      ? 'opacity-60 cursor-not-allowed'
                      : 'cursor-grab active:cursor-grabbing hover:bg-muted/60'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <GripVertical className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                    <div>
                      <div className="font-medium leading-tight">{block.label}</div>
                      {block.phase ? (
                        <Badge variant="outline" className="mt-1 text-[10px] uppercase">
                          {block.phase.replace(/_/g, ' ')}
                        </Badge>
                      ) : null}
                      {block.description ? (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                          {block.description}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-1 xl:col-span-1">
          <CardHeader>
            <CardTitle>
              {isNew ? 'Create template' : readOnlyBuiltin ? 'View default template' : 'Edit template'}
            </CardTitle>
            <CardDescription>
              {readOnlyBuiltin
                ? 'This is the shipped Halo default (read-only). Use “Duplicate as custom” or a “New … template” button to reorder blocks and save.'
                : 'Drag blocks from the palette (wide screens) or use Add block / dropdowns. Built-in defaults stay read-only until duplicated.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Template ID</Label>
                <Input
                  className="mt-1"
                  value={form.template_id}
                  disabled={!isNew || readOnlyBuiltin}
                  onChange={(e) => setForm((prev) => ({ ...prev, template_id: e.target.value }))}
                  placeholder="acme_nup"
                />
              </div>
              <div>
                <Label>Agent kind</Label>
                <Select
                  value={form.agent}
                  onValueChange={(value) => {
                    setForm((prev) => {
                      if (!isNew) {
                        return { ...prev, agent: value };
                      }
                      const seed = defaultBuiltinForAgent(templates, value);
                      return {
                        ...prev,
                        agent: value,
                        template_id: `${value}_custom`,
                        steps: (seed?.steps || []).map(normalizeStep),
                      };
                    });
                  }}
                  disabled={!isNew || readOnlyBuiltin}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WORKFLOW_AGENT_KINDS.map((kind) => (
                      <SelectItem key={kind.id} value={kind.id}>
                        {kind.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label>Label</Label>
                <Input
                  className="mt-1"
                  value={form.label}
                  disabled={readOnlyBuiltin}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      label: e.target.value,
                      template_id: isNew ? slugify(e.target.value) || prev.template_id : prev.template_id,
                    }))
                  }
                />
              </div>
              <div className="md:col-span-2">
                <Label>Description</Label>
                <Textarea
                  className="mt-1"
                  rows={2}
                  disabled={readOnlyBuiltin}
                  value={form.description}
                  onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={form.require_user_confirmation}
                  disabled={readOnlyBuiltin}
                  onCheckedChange={(value) =>
                    setForm((prev) => ({ ...prev, require_user_confirmation: Boolean(value) }))
                  }
                />
                Require user confirmation (NUP)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={form.allow_scheduled_termination}
                  disabled={readOnlyBuiltin}
                  onCheckedChange={(value) =>
                    setForm((prev) => ({ ...prev, allow_scheduled_termination: Boolean(value) }))
                  }
                />
                Allow scheduled termination (TER)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={form.prefer_sod_check}
                  disabled={readOnlyBuiltin}
                  onCheckedChange={(value) =>
                    setForm((prev) => ({ ...prev, prefer_sod_check: Boolean(value) }))
                  }
                />
                Prefer SoD check
              </label>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Block sequence</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={addStep}
                  disabled={readOnlyBuiltin}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add block
                </Button>
              </div>
              <div
                className="min-h-[120px] rounded-lg border border-dashed p-2"
                data-testid="workflow-sequence-dropzone"
                onDragOver={(event) => {
                  allowDrop(event);
                }}
                onDragLeave={(event) => {
                  if (event.currentTarget.contains(event.relatedTarget)) return;
                  endSequenceDrag();
                }}
              >
                {form.steps.length === 0 && !readOnlyBuiltin ? (
                  <div
                    className={`rounded-md py-8 px-4 text-center ${
                      dropTargetIndex === 0
                        ? 'border-2 border-dashed border-primary bg-primary/5'
                        : ''
                    }`}
                    onDragOver={(event) => {
                      event.stopPropagation();
                      allowDrop(event);
                      setDropTargetIndex(0);
                      setSequenceDragActive(true);
                    }}
                    onDrop={(event) => handleSequenceDrop(event, 0)}
                  >
                    <p className="text-sm text-muted-foreground">
                      Drop blocks here (wide window: block palette on the left) or click Add block.
                    </p>
                  </div>
                ) : null}
                {form.steps.length > 0 ? renderInsertSlot(0) : null}
                {form.steps.map((step, index) => {
                  const blockMeta = blockCatalog.find((b) => b.block_id === step.block_id);
                  return (
                    <React.Fragment key={`${step.block_id}-${index}`}>
                    <div
                      draggable={!readOnlyBuiltin}
                      onDragStart={(e) => startStepDrag(e, index)}
                      onDragEnd={endSequenceDrag}
                      className={`rounded-lg border p-3 space-y-2 ${
                        readOnlyBuiltin ? '' : 'cursor-grab active:cursor-grabbing'
                      }`}
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="text-xs text-muted-foreground w-6">{index + 1}.</span>
                        <Select
                          value={step.block_id}
                          disabled={readOnlyBuiltin}
                          onValueChange={(value) => updateStep(index, { block_id: value })}
                        >
                          <SelectTrigger className="w-[240px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {blocksForAgent(form.agent).map((block) => (
                              <SelectItem key={block.block_id} value={block.block_id}>
                                {block.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <label className="flex items-center gap-2 text-xs">
                          <Checkbox
                            checked={step.optional}
                            disabled={readOnlyBuiltin}
                            onCheckedChange={(value) => updateStep(index, { optional: Boolean(value) })}
                          />
                          Optional
                        </label>
                        <div className="ml-auto flex gap-1">
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            disabled={readOnlyBuiltin}
                            onClick={() => moveStep(index, -1)}
                          >
                            <ArrowUp className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            disabled={readOnlyBuiltin}
                            onClick={() => moveStep(index, 1)}
                          >
                            <ArrowDown className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            disabled={readOnlyBuiltin}
                            onClick={() => removeStep(index)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      {blockMeta?.description ? (
                        <p className="text-xs text-muted-foreground pl-8">{blockMeta.description}</p>
                      ) : null}
                      <Input
                        className="ml-8"
                        placeholder="Optional note for operators / prompts"
                        disabled={readOnlyBuiltin}
                        value={step.note}
                        onChange={(e) => updateStep(index, { note: e.target.value })}
                      />
                    </div>
                      {renderInsertSlot(index + 1)}
                    </React.Fragment>
                  );
                })}
              </div>
              <details className="xl:hidden mt-3 rounded-lg border p-3">
                <summary className="text-sm font-medium cursor-pointer">Block palette (tap to add)</summary>
                <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
                  {blocksForAgent(form.agent).map((block) => (
                    <Button
                      key={block.block_id}
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full justify-start h-auto py-2"
                      disabled={readOnlyBuiltin}
                      onClick={() => insertBlockAt(form.steps.length, block.block_id)}
                    >
                      {block.label}
                    </Button>
                  ))}
                </div>
              </details>
            </div>

            {validationErrors.length > 0 && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                {validationErrors.map((err) => (
                  <div key={err}>{err}</div>
                ))}
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <span title={permissionDeniedTitle(canUpdate, 'save workflow templates')}>
                <Button
                  onClick={handleSave}
                  disabled={!canUpdate || saving || !form.template_id || readOnlyBuiltin}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving…' : isNew ? 'Create template' : 'Save changes'}
                </Button>
              </span>
              {!isNew && selectedTemplate?.is_builtin && (
                <Button variant="outline" onClick={() => duplicateFromBuiltin(selectedTemplate)}>
                  Duplicate as custom
                </Button>
              )}
              {!isNew && selectedTemplate && !selectedTemplate.is_builtin && (
                <span title={permissionDeniedTitle(canUpdate, 'delete workflow templates')}>
                  <Button variant="destructive" onClick={handleDelete} disabled={!canUpdate || saving}>
                    Delete
                  </Button>
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
